import { ChevronUpIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo } from 'react';

import type { ViewModeDropupProps } from './ViewModeDropup.types';
import type { ViewModeOption } from './ViewModeDropup.types';
import type { ViewMode } from '@/types/layout';

import { Listbox } from '@/components/ui/Listbox';

/**
 * Available view mode options (trainer option filtered by role)
 */
const BASE_VIEW_MODE_OPTIONS: ViewModeOption[] = [
  { id: 'default', label: 'Default' },
  { id: 'social', label: 'Social' },
  { id: 'analytics', label: 'Analytics' },
];

const TRAINER_VIEW_MODE_OPTION: ViewModeOption = {
  id: 'trainer',
  label: 'Trainer',
};

/**
 * ViewModeDropup - the dashboard view-mode switcher, pinned to the bottom of
 * the sidebar.
 *
 * What is left here after {@link Listbox} took the widget is what is actually
 * about view modes: which options exist for this role, and the sidebar's own
 * trigger — an icon that survives the collapsed rail, where the label and
 * chevron do not.
 *
 * @example
 * ```tsx
 * <ViewModeDropup
 *   value="default"
 *   onChange={(mode) => setViewMode(mode)}
 * />
 * ```
 */
export function ViewModeDropup({
  value = 'default',
  onChange,
  collapsed = false,
  userRole,
  className = '',
}: ViewModeDropupProps): React.JSX.Element {
  // Build options list - trainer option only for trainer role
  const viewModeOptions = useMemo<ViewModeOption[]>(() => {
    if (userRole === 'trainer') {
      return [...BASE_VIEW_MODE_OPTIONS, TRAINER_VIEW_MODE_OPTION];
    }
    return BASE_VIEW_MODE_OPTIONS;
  }, [userRole]);

  const options = useMemo(
    () => viewModeOptions.map(({ id, label }) => ({ value: id, label })),
    [viewModeOptions]
  );

  const currentLabel =
    viewModeOptions.find((option) => option.id === value)?.label ?? 'Default';

  const handleChange = useCallback(
    (next: string) => onChange?.(next as ViewMode),
    [onChange]
  );

  return (
    <Listbox
      value={value}
      options={options}
      onChange={handleChange}
      label="View mode"
      placement="top"
      className={`view-mode-dropup ${className}`.trim()}
      panelClassName={collapsed ? 'min-w-[160px]' : ''}
      renderTrigger={({ isOpen, triggerProps }) => (
        <button
          {...triggerProps}
          aria-label={collapsed ? `View mode: ${currentLabel}` : undefined}
          className={`
          flex w-full items-center gap-3 rounded-lg
          bg-contrast-700 px-3 py-2.5
          text-neutral-300 transition-all duration-fast
          hover:bg-contrast-600 hover:text-neutral-50
          focus-visible:outline-none focus-visible:ring-state-focus
          ${collapsed ? 'justify-center' : ''}
        `}
        >
          <Squares2X2Icon className="h-5 w-5 shrink-0 text-primary-300" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-label-md font-medium">
                {currentLabel}
              </span>
              <ChevronUpIcon
                className={`
                h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-normal
                ${isOpen ? 'rotate-180' : ''}
              `}
              />
            </>
          )}
        </button>
      )}
    />
  );
}

export default ViewModeDropup;
